import {
  Component,
  ChangeDetectionStrategy,
  Input,
  ViewChild,
  HostBinding,
  ElementRef,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { FormGroup, FormBuilder } from '@angular/forms';
import { BehaviorSubject, Observable, merge, combineLatest } from 'rxjs';
import { filter, map, scan, take } from 'rxjs/operators';
import { decode, encode } from 'he';

import { AuthService } from '../../auth';
import { ChatService } from '../services';
import { ChatMessage, ChatRoom, EmojiDto } from '../models';
import { SubSink } from 'subsink';
import { ChatFavouritesComponent } from './chat-favourites.component';
import { ChatUserListComponent } from './chat-user-list.component';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

@Component({
  selector: 'lib-chat',
  templateUrl: './chat.component.html',
  styles: [
    `
      .nav-link-mini {
        padding: 3px;
        font-size: smaller;
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChatComponent implements OnInit, OnDestroy {
  //@ViewChildren('chatMessages') chatMessages?: QueryList<any>;
  //@ViewChild(PerfectScrollbarDirective, { static: false })
  //chatScroll?: PerfectScrollbarDirective;
  @HostBinding('class') class = 'd-flex flex-column flex-grow-1';
  @ViewChild('msg') msg?: ElementRef<HTMLInputElement>;

  @Input()
  set campaignId(value: number | null | undefined) {
    if (!!value && value > 0) {
      combineLatest([this.chatSvc.activeRoom$, this.chatSvc.rooms$])
        .pipe(take(1))
        .subscribe(([activeRoom, rooms]) => {
          const campaignRoom = rooms?.find((r) =>
            r.campaignIds?.includes(value)
          );
          if (!!campaignRoom && activeRoom !== campaignRoom.group) {
            this.selectRoom(campaignRoom.group);
          }
        });
    }
  }
  @Input() showAllChat: boolean = false;
  @Input() showSettings: boolean = false;

  private allchatChatrooms: string[] = [];
  private showEmojis = new BehaviorSubject<boolean>(false);
  private subs = new SubSink();

  chatForm: FormGroup;
  showChatUserList: boolean;
  viewModel$: Observable<ViewModel>;

  constructor(
    private chatSvc: ChatService,
    private authSvc: AuthService,
    private fb: FormBuilder,
    private modalService: NgbModal
  ) {
    this.showChatUserList = false;

    this.chatSvc.showUserList$.subscribe((showFlag) => {
      this.showChatUserList = showFlag;
    });

    this.chatSvc.allchat$
      .pipe(filter((allchat) => allchat !== undefined))
      .subscribe((allchat) => {
        this.allchatChatrooms = allchat!.map((a) => a.group);
      });

    this.chatForm = this.fb.group({
      msg: [''],
      room: [''],
    });
    this.viewModel$ = this.initViewModel();

    this.chatForm.controls['room'].valueChanges.subscribe((newroom) => {
      this.chatSvc.setAllChatRoom(newroom);
    });

    combineLatest([this.chatSvc.rooms$, this.chatSvc.activeRoom$]).subscribe(
      ([rooms, activeRoom]) => {
        console.log('Rooms:', rooms, 'activeRoom:', activeRoom);

        if (
          this.showAllChat &&
          activeRoom === 'All Chat' &&
          rooms != undefined
        ) {
          console.log(
            'patchValue:',
            rooms!.filter((rm) => rm.group !== 'All Chat')[0].group
          );

          this.chatForm.patchValue({
            room: rooms!.filter((rm) => rm.group !== 'All Chat')[0].group,
          });
        }
      }
    );
  }
  private initViewModel() {
    return merge<Partial<ViewModel>>(
      this.chatSvc.messages$.pipe(
        map((messages) => ({
          messages: messages?.filter((m) => !m.pinned),
          pinned: messages?.filter((m) => m.pinned),
        }))
      ),
      this.chatSvc.rooms$.pipe(map((rooms) => ({ rooms }))),
      this.chatSvc.emojis$.pipe(map((emojis) => ({ emojis }))),
      this.chatSvc.activeRoom$.pipe(map((activeRoom) => ({ activeRoom }))),
      this.chatSvc.readOnly$.pipe(map((readOnly) => ({ readOnly }))),
      this.chatSvc.favourites$.pipe(map((favourites) => ({ favourites }))),
      this.chatSvc.allchat$.pipe(map((allchat) => ({ allchat }))),
      this.showEmojis.pipe(map((showEmojis) => ({ showEmojis }))),
      this.authSvc.userId$.pipe(map((currentUserId) => ({ currentUserId })))
    ).pipe(scan((acc, value) => ({ ...acc, ...value }), {} as ViewModel));
  }

  addEmojiToMsgbox(emoji: string) {
    const inp = this.msg?.nativeElement;
    if (inp) {
      var unicodeEmoji = decode(emoji);
      var cursorPos = inp.selectionStart;
      var existingMsg = (this.chatForm.controls.msg.value as string) || '';
      if (!cursorPos) cursorPos = existingMsg.length;
      var newMsg = `${existingMsg.substring(
        0,
        cursorPos
      )}${unicodeEmoji}${existingMsg.substring(cursorPos)}`;
      this.chatForm.controls.msg.setValue(newMsg);
      inp.focus();
      inp.setSelectionRange(cursorPos + 2, cursorPos + 2);
    }
  }
  closePersonalChat(personalRoom: string) {
    this.chatSvc.closePersonalChat(personalRoom);
    this.chatSvc.activeRoom$.pipe(take(1)).subscribe((activeroom) => {
      if (activeroom !== 'All Chat')
        this.chatForm.patchValue({ room: activeroom });
    });
  }

  msgPinnedPredicate(room: string | undefined) {
    return (msg: ChatMessage) =>
      msg.group === room ||
      (room === 'All Chat' &&
        msg.group === this.chatForm.controls['room'].value);
  }

  msgPredicate(room: string | undefined) {
    return (msg: ChatMessage) =>
      msg.group === room ||
      (room === 'All Chat' && this.allchatChatrooms.includes(msg.group));
  }

  //ngAfterViewInit() {
  //this.chatScroll?.scrollToBottom();
  //this.chatMessages?.changes.subscribe(() =>
  //  this.chatScroll?.scrollToBottom()
  //);
  //}

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  ngOnInit(): void {
    if (this.showAllChat) {
      this.chatSvc.selectRoom('All Chat');
    }
  }

  openSettings() {
    this.modalService.open(ChatFavouritesComponent, {
      size: 'md',
      backdrop: 'static',
      keyboard: false,
      scrollable: true,
    });
  }

  selectRoom(room: string) {
    this.chatSvc.selectRoom(room);
  }

  sendMsg(room: string | undefined, showSettings: boolean) {
    const msg = encode(this.chatForm.value.msg, {
      decimal: true,
      useNamedReferences: true,
    });
    if (room && msg) {
      if (this.showAllChat && room === 'All Chat') {
        room = this.chatForm.get('room')?.value as string;
      }

      if (msg === '/close' && room.startsWith('U') && showSettings) {
        this.closePersonalChat(room);
        this.chatForm.controls['msg'].reset();
      } else if (msg.toLowerCase() === '/who') {
        this.chatSvc.showUserList(true);
        //this.chatSvc.openUserList();
        this.chatForm.controls['msg'].reset();
      } else {
        this.chatSvc.send(msg, room, showSettings);
        this.chatForm.reset();
        this.chatForm.patchValue({ room: room });
      }
    }
  }

  showUserList() {
    this.chatSvc.showUserList(true);
    this.chatForm.controls['msg'].reset();
  }

  toggleEmojis() {
    this.showEmojis.next(!this.showEmojis.value);
  }
}

interface ViewModel {
  activeRoom: string | undefined;
  allchat: ChatRoom[] | undefined;
  currentUserId: number | undefined;
  emojis: EmojiDto[] | undefined;
  favourites: ChatRoom[] | undefined;
  messages: ChatMessage[] | undefined;
  pinned: ChatMessage[] | undefined;
  readOnly: boolean;
  rooms: ChatRoom[] | undefined;
  showEmojis: boolean;
}
